import type React from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  GripVertical,
  RefreshCw,
  SlidersHorizontal,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  useProviderCheckoutFieldsSettings,
  useUpdateProviderCheckoutFieldsSettings,
} from '@/hooks/provider/useProviderCheckoutFieldsSettings';
import type {
  ProviderCheckoutFieldSetting,
  ProviderCountryOption,
} from '@/types/provider-checkout-fields';
import {
  CHECKOUT_FIELD_KEYS,
  ProviderCheckoutFieldsRequestError,
} from '@/services/provider/checkout-fields.service';

const applyOrder = (list: ProviderCheckoutFieldSetting[]): ProviderCheckoutFieldSetting[] =>
  list.map((field, index) => ({
    ...field,
    order: (index + 1) * 10,
  }));

export function ProviderCheckoutFieldsCard() {
  const { t, i18n } = useTranslation();
  const settingsQuery = useProviderCheckoutFieldsSettings(true, i18n.language);
  const updateMutation = useUpdateProviderCheckoutFieldsSettings();
  const idBase = useId();

  const [fields, setFields] = useState<ProviderCheckoutFieldSetting[]>([]);
  const [enabledCountries, setEnabledCountries] = useState<string[]>([]);
  const [priorityCountries, setPriorityCountries] = useState<string[]>([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [prioritySearch, setPrioritySearch] = useState('');
  const [countryErrors, setCountryErrors] = useState<{ enabled: string[]; priority: string[] }>({
    enabled: [],
    priority: [],
  });
  const [draggedKey, setDraggedKey] = useState<ProviderCheckoutFieldSetting['key'] | null>(
    null,
  );

  useEffect(() => {
    if (settingsQuery.data) {
      const ordered = [...settingsQuery.data.fields].sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        return a.key.localeCompare(b.key);
      });
      setFields(applyOrder(ordered));
      setEnabledCountries(settingsQuery.data.enabledCountries);
      setPriorityCountries(settingsQuery.data.priorityCountries);
      setCountryErrors({ enabled: [], priority: [] });
    }
  }, [settingsQuery.data]);

  const isSaving = updateMutation.isPending;

  const labels = useMemo(
    () =>
      CHECKOUT_FIELD_KEYS.reduce<Record<string, string>>((acc, key) => {
        acc[key] = t(`settings.checkoutFields.fields.${key}`, key);
        return acc;
      }, {}),
    [t],
  );

  const setField = (
    key: ProviderCheckoutFieldSetting['key'],
    updater: (field: ProviderCheckoutFieldSetting) => ProviderCheckoutFieldSetting,
  ) => {
    setFields((prev) =>
      prev.map((field) => (field.key === key ? updater(field) : field)),
    );
  };

  const availableCountries = settingsQuery.data?.availableCountries ?? [];
  const countryNameMap = useMemo(
    () =>
      availableCountries.reduce<Record<string, string>>((acc, option) => {
        acc[option.code] = option.name;
        return acc;
      }, {}),
    [availableCountries],
  );

  const enabledCountrySet = useMemo(() => new Set(enabledCountries), [enabledCountries]);
  const priorityCountryOptions = useMemo(
    () => availableCountries.filter((country) => enabledCountrySet.has(country.code)),
    [availableCountries, enabledCountrySet],
  );

  const searchCountries = (countries: ProviderCountryOption[], query: string) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return countries;
    }
    return countries.filter((country) => {
      const code = country.code.toLowerCase();
      const name = country.name.toLowerCase();
      return code.includes(normalized) || name.includes(normalized);
    });
  };

  const filteredAvailableCountries = useMemo(
    () => searchCountries(availableCountries, countrySearch),
    [availableCountries, countrySearch],
  );
  const filteredPriorityCountries = useMemo(
    () => searchCountries(priorityCountryOptions, prioritySearch),
    [priorityCountryOptions, prioritySearch],
  );
  const orderedPriorityCountries = useMemo(
    () =>
      priorityCountries.filter((code) => enabledCountrySet.has(code)).map((code) => ({
        code,
        name: countryNameMap[code] ?? code,
      })),
    [priorityCountries, enabledCountrySet, countryNameMap],
  );

  const handleReorder = (
    sourceKey: ProviderCheckoutFieldSetting['key'],
    targetKey: ProviderCheckoutFieldSetting['key'],
  ) => {
    if (sourceKey === targetKey) {
      return;
    }
    setFields((prev) => {
      const sourceIndex = prev.findIndex((field) => field.key === sourceKey);
      const targetIndex = prev.findIndex((field) => field.key === targetKey);
      if (sourceIndex === -1 || targetIndex === -1) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return applyOrder(next);
    });
  };

  const handleEnabledCountryToggle = (code: string, checked: boolean) => {
    setCountryErrors((prev) => ({ ...prev, enabled: [] }));
    setEnabledCountries((prev) => {
      if (checked) {
        return prev.includes(code) ? prev : [...prev, code];
      }

      const next = prev.filter((item) => item !== code);
      setPriorityCountries((current) => current.filter((item) => item !== code));
      return next;
    });
  };

  const handlePriorityCountryToggle = (code: string, checked: boolean) => {
    setCountryErrors((prev) => ({ ...prev, priority: [] }));
    setPriorityCountries((prev) => {
      if (!enabledCountrySet.has(code)) {
        return prev;
      }
      if (checked) {
        return prev.includes(code) ? prev : [...prev, code];
      }
      return prev.filter((item) => item !== code);
    });
  };

  const movePriorityCountry = (code: string, direction: 'up' | 'down') => {
    setCountryErrors((prev) => ({ ...prev, priority: [] }));
    setPriorityCountries((prev) => {
      const index = prev.indexOf(code);
      if (index === -1) {
        return prev;
      }

      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) {
        return prev;
      }

      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };

  const collectCountryErrors = (
    error: ProviderCheckoutFieldsRequestError,
  ): { enabled: string[]; priority: string[] } => {
    const enabled: string[] = [];
    const priority: string[] = [];
    const validationErrors = error.errors ?? {};

    Object.entries(validationErrors).forEach(([key, messages]) => {
      if (!Array.isArray(messages) || messages.length === 0) {
        return;
      }
      if (key === 'enabled_countries' || key.startsWith('enabled_countries.')) {
        enabled.push(...messages);
      }
      if (key === 'priority_countries' || key.startsWith('priority_countries.')) {
        priority.push(...messages);
      }
    });

    return { enabled, priority };
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const normalized = applyOrder(fields).map((field) => {
      const resolvedEnabled = field.required ? true : field.enabled;
      return {
        ...field,
        enabled: resolvedEnabled,
        required: resolvedEnabled ? field.required : false,
      };
    });

    try {
      const filteredEnabledCountries = enabledCountries.filter((code) => countryNameMap[code]);
      const filteredPriorityCountries = priorityCountries.filter((code) =>
        filteredEnabledCountries.includes(code),
      );
      const updated = await updateMutation.mutateAsync({
        fields: normalized,
        enabledCountries: filteredEnabledCountries,
        priorityCountries: filteredPriorityCountries,
      });
      const ordered = [...updated.fields].sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        return a.key.localeCompare(b.key);
      });
      setFields(applyOrder(ordered));
      setEnabledCountries(updated.enabledCountries);
      setPriorityCountries(updated.priorityCountries);
      setCountryErrors({ enabled: [], priority: [] });
      toast.success(
        t('settings.checkoutFields.messages.saveSuccess', 'Checkout fields updated.'),
      );
    } catch (error) {
      if (error instanceof ProviderCheckoutFieldsRequestError) {
        setCountryErrors(collectCountryErrors(error));
      }
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('settings.checkoutFields.messages.saveError', 'Unable to update checkout fields.');
      toast.error(message);
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
            {t('settings.checkoutFields.title', 'Checkout fields')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.checkoutFields.description',
              'Choose which fields appear on checkout and which are required.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('settings.checkoutFields.title', 'Checkout fields')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.checkoutFields.description',
              'Choose which fields appear on checkout and which are required.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTitle>
              {t('settings.checkoutFields.messages.loadError', 'Unable to load checkout fields.')}
            </AlertTitle>
            <AlertDescription className="mt-2 flex items-center gap-3">
              <span>{settingsQuery.error.message}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => settingsQuery.refetch()}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
          {t('settings.checkoutFields.title', 'Checkout fields')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.checkoutFields.description',
            'Choose which fields appear on checkout and which are required.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <p className="text-sm text-muted-foreground">
            {t('settings.checkoutFields.helper', 'Drag rows to reorder checkout fields.')}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>{t('settings.checkoutFields.columns.field', 'Field')}</TableHead>
                <TableHead className="text-center">
                  {t('settings.checkoutFields.columns.enabled', 'Enabled')}
                </TableHead>
                <TableHead className="text-center">
                  {t('settings.checkoutFields.columns.required', 'Required')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field) => {
                const isDragging = draggedKey === field.key;
                const enabledId = `${idBase}-${field.key}-enabled`;
                const requiredId = `${idBase}-${field.key}-required`;
                return (
                  <TableRow
                    key={field.key}
                    className={isDragging ? 'opacity-60' : undefined}
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const sourceKey =
                        (event.dataTransfer.getData('text/plain') as ProviderCheckoutFieldSetting['key']) ||
                        draggedKey;
                      if (sourceKey) {
                        handleReorder(sourceKey, field.key);
                      }
                      setDraggedKey(null);
                    }}
                  >
                    <TableCell className="w-10">
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-muted-foreground hover:border-border hover:bg-muted/40"
                        draggable
                        onDragStart={(event) => {
                          setDraggedKey(field.key);
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', field.key);
                        }}
                        onDragEnd={() => setDraggedKey(null)}
                        aria-label={t('settings.checkoutFields.columns.reorder', 'Reorder')}
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{labels[field.key]}</p>
                        <p className="text-xs text-muted-foreground">{field.key}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          id={enabledId}
                          checked={field.enabled}
                          onCheckedChange={(checked) =>
                            setField(field.key, (current) => ({
                              ...current,
                              enabled: checked,
                              required: checked ? current.required : false,
                            }))
                          }
                          disabled={isSaving}
                        />
                        <Label htmlFor={enabledId} className="sr-only">
                          {t('settings.checkoutFields.columns.enabled', 'Enabled')}
                        </Label>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          id={requiredId}
                          checked={field.required}
                          onCheckedChange={(checked) =>
                            setField(field.key, (current) => ({
                              ...current,
                              enabled: checked ? true : current.enabled,
                              required: checked,
                            }))
                          }
                          disabled={isSaving}
                        />
                        <Label htmlFor={requiredId} className="sr-only">
                          {t('settings.checkoutFields.columns.required', 'Required')}
                        </Label>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <div className="space-y-3 rounded-lg border p-4">
            <div>
              <p className="text-sm font-medium">
                {t('settings.checkoutFields.countries.title', 'Country availability')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.checkoutFields.countries.description',
                  'Choose which countries are available on checkout and which should appear as priority.',
                )}
              </p>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t('settings.checkoutFields.countries.enabledLabel', 'Enabled countries')}
                </Label>
                <Input
                  value={countrySearch}
                  onChange={(event) => setCountrySearch(event.target.value)}
                  placeholder={t('settings.checkoutFields.countries.searchPlaceholder', 'Search country')}
                  disabled={isSaving}
                />
                <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border p-2">
                  {filteredAvailableCountries.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('settings.checkoutFields.countries.empty', 'No countries found.')}
                    </p>
                  ) : (
                    filteredAvailableCountries.map((country) => (
                      <Label
                        key={country.code}
                        className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-muted/40"
                      >
                        <span className="text-sm">
                          {country.name} ({country.code})
                        </span>
                        <Checkbox
                          checked={enabledCountrySet.has(country.code)}
                          onCheckedChange={(checked) =>
                            handleEnabledCountryToggle(country.code, checked === true)
                          }
                          disabled={isSaving}
                        />
                      </Label>
                    ))
                  )}
                </div>
                {countryErrors.enabled.map((message) => (
                  <p key={`enabled-error-${message}`} className="text-xs text-destructive">
                    {message}
                  </p>
                ))}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t('settings.checkoutFields.countries.priorityLabel', 'Priority countries')}
                </Label>
                <div className="space-y-2 rounded-md border p-2">
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'settings.checkoutFields.countries.priorityOrderHelp',
                      'Use arrows to set the display order for priority countries.',
                    )}
                  </p>
                  {orderedPriorityCountries.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'settings.checkoutFields.countries.priorityEmpty',
                        'Enable countries first to choose priority countries.',
                      )}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {orderedPriorityCountries.map((country, index) => (
                        <div
                          key={`priority-order-${country.code}`}
                          className="flex items-center justify-between gap-2 rounded border px-2 py-1.5"
                        >
                          <span className="text-sm">
                            {country.name} ({country.code})
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => movePriorityCountry(country.code, 'up')}
                              disabled={isSaving || index === 0}
                              aria-label={t('settings.checkoutFields.countries.moveUp', 'Move up')}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => movePriorityCountry(country.code, 'down')}
                              disabled={isSaving || index === orderedPriorityCountries.length - 1}
                              aria-label={t('settings.checkoutFields.countries.moveDown', 'Move down')}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Input
                  value={prioritySearch}
                  onChange={(event) => setPrioritySearch(event.target.value)}
                  placeholder={t('settings.checkoutFields.countries.searchPlaceholder', 'Search country')}
                  disabled={isSaving}
                />
                <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border p-2">
                  {filteredPriorityCountries.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('settings.checkoutFields.countries.empty', 'No countries found.')}
                    </p>
                  ) : (
                    filteredPriorityCountries.map((country) => (
                      <Label
                        key={country.code}
                        className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-muted/40"
                      >
                        <span className="text-sm">
                          {country.name} ({country.code})
                        </span>
                        <Checkbox
                          checked={priorityCountries.includes(country.code)}
                          onCheckedChange={(checked) =>
                            handlePriorityCountryToggle(country.code, checked === true)
                          }
                          disabled={isSaving}
                        />
                      </Label>
                    ))
                  )}
                </div>
                {countryErrors.priority.map((message) => (
                  <p key={`priority-error-${message}`} className="text-xs text-destructive">
                    {message}
                  </p>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFields(applyOrder(settingsQuery.data?.fields ?? []));
                setEnabledCountries(settingsQuery.data?.enabledCountries ?? []);
                setPriorityCountries(settingsQuery.data?.priorityCountries ?? []);
                setCountryErrors({ enabled: [], priority: [] });
              }}
              disabled={isSaving}
            >
              {t('common.clear', 'Clear')}
            </Button>
            <Button type="submit" disabled={isSaving} className="gap-2">
              {isSaving && <CheckCircle2 className="h-4 w-4 animate-spin" />}
              {t('settings.checkoutFields.actions.save', 'Save fields')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
